import React, { useId } from "react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { fmtINR, fmtPct } from "@/lib/format";

// Tiny inline trend sparkline — pure SVG, no chart library.
// `data` is an array of numbers (oldest → newest). Colour is mint when the
// series ends at/above where it started, rust otherwise. Hovering reveals a
// tooltip with the recent high, low and % change.
export const Sparkline = ({ data, width = 68, height = 24, strokeWidth = 1.5, testid }) => {
  const gradId = useId();
  if (!Array.isArray(data) || data.length < 2) {
    return (
      <span
        className="inline-block"
        style={{ width, height, opacity: 0.4 }}
        data-testid={testid}
        aria-hidden="true"
      >
        <svg width={width} height={height}>
          <line
            x1="2" y1={height / 2} x2={width - 2} y2={height / 2}
            stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="2 2"
          />
        </svg>
      </span>
    );
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const padY = strokeWidth + 1;
  const usableH = height - padY * 2;
  const stepX = (width - 2) / (data.length - 1);

  const points = data.map((v, i) => {
    const x = 1 + i * stepX;
    const y = padY + (1 - (v - min) / range) * usableH;
    return [x, y];
  });

  const linePath = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const areaPath = `${linePath} L${points[points.length - 1][0].toFixed(2)},${height} L${points[0][0].toFixed(2)},${height} Z`;

  const first = data[0];
  const last = data[data.length - 1];
  const positive = last >= first;
  const color = positive ? "var(--accent-positive)" : "var(--accent-negative)";
  const changeAbs = last - first;
  const changePct = first ? (changeAbs / first) * 100 : 0;
  const toneClass = positive ? "text-positive" : "text-negative";

  const svg = (
    <span className="inline-block align-middle cursor-default" data-testid={testid}>
      <svg width={width} height={height} style={{ display: "block", overflow: "visible" }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle
          cx={points[points.length - 1][0]}
          cy={points[points.length - 1][1]}
          r={strokeWidth + 0.5}
          fill={color}
        />
      </svg>
    </span>
  );

  return (
    <TooltipProvider delayDuration={80}>
      <Tooltip>
        <TooltipTrigger asChild>{svg}</TooltipTrigger>
        <TooltipContent
          className="border shadow-none"
          style={{
            background: "var(--bg-surface)",
            color: "var(--text-primary)",
            borderColor: "var(--border-soft)",
          }}
          data-testid={testid ? `${testid}-tooltip` : "sparkline-tooltip"}
        >
          <div className="min-w-[130px] py-0.5">
            <div className="label-overline mb-1.5">{data.length}-Day Trend</div>
            <div className="flex items-center justify-between gap-4 font-mono text-[11px]">
              <span style={{ color: "var(--text-muted)" }}>High</span>
              <span>{fmtINR(max)}</span>
            </div>
            <div className="flex items-center justify-between gap-4 font-mono text-[11px]">
              <span style={{ color: "var(--text-muted)" }}>Low</span>
              <span>{fmtINR(min)}</span>
            </div>
            <div
              className="flex items-center justify-between gap-4 font-mono text-[11px] mt-1 pt-1"
              style={{ borderTop: "1px solid var(--border-soft)" }}
            >
              <span style={{ color: "var(--text-muted)" }}>Change</span>
              <span className={toneClass}>
                {fmtPct(changePct)} · {fmtINR(changeAbs, { showSign: true })}
              </span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default Sparkline;
